import { Data, Model } from '@singularsystems/neo-core';
import { AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../DomainTypes';
import InterestRateCalculationType from '../Models/Rates/InterestRateCalculationType';
import InterestRateType from '../Models/Rates/InterestRateType';
import InterestRate from '../Models/Rates/InterestRate';
import InterestRatePagedLookupCriteria from '../Models/Rates/Lookups/InterestRatePagedLookupCriteria';
import DateCycleLookup from '../Models/Dividends/Lookups/DateCycleLookup';
import DateCycle from '../Models/Dividends/DateCycle';
import AddChildInterestRateTypeCommand from '../Models/Rates/Commands/AddChildInterestRateTypeCommand';

export interface IInvestmentSetupApiClient {

    /**
     * Gets a list of interest rate calculation types
     */
    getInterestRateCalculationTypes(): AxiosPromise<Array<Model.PlainTrackedObject<InterestRateCalculationType>>>;

    /**
     * Saves an interest rate calculation type to the database
     * @param interestRateCalculationType The interest rate calculation type to be saved
     * @returns The saved InterestRateCalculationType
     */
    saveInterestRateCalculationType(interestRateCalculationType: Model.PartialPlainObject<InterestRateCalculationType>): AxiosPromise<Model.PlainTrackedObject<InterestRateCalculationType>>;

    /**
     * Deletes an interest rate calculation type from the database and removes it from the rate formula cache
     * @param interestRateCalculationTypeId The id of the interest rate calculation type to be deleted
     * @returns The saved list of InterestRateCalculationTypes
     */
    deleteInterestRateCalculationType(interestRateCalculationTypeId: number): AxiosPromise;

    /** 
     * Gets a list of interest rate types **without** loading the linked rates.
     */
    getInterestRateTypes(includeChildren?: boolean): AxiosPromise<Array<Model.PlainTrackedObject<InterestRateType>>>;

    /**
     * Gets a paged list of interest rate lookups
     * @param request The page request containing filter criteria
     * @returns A paged list of
     */
    getInterestRateLookupsPaged(request: Model.PartialPlainObject<Data.PageRequest<InterestRatePagedLookupCriteria>>): AxiosPromise<Data.PageResult<Model.PlainTrackedObject<InterestRate>>>;

    /**
     * Saves an interest rate type to the database
     * @param interestRateType The interest rate type to be saved
     * @returns The saved InterestRateType
     */
    saveInterestRateTypes(interestRateType: Array<Model.PartialPlainObject<InterestRateType>>): AxiosPromise<Array<Model.PlainTrackedObject<InterestRateType>>>;

    /**
     * Saves an interest rate to the database
     * @param interestRate The interest rate to be saved
     * @returns The saved InterestRate list
     */
    saveInterestRates(interestRate: Array<Model.PartialPlainObject<InterestRate>>): AxiosPromise<Array<Model.PlainTrackedObject<InterestRate>>>;

    /**
     * Gets a list of date cycles without dates.
     */
    getDateCycles(): AxiosPromise<Array<Model.PlainObject<DateCycleLookup>>>;

    /**
     * Gets a single date cycle with it's child date list.
     */
    getDateCycle(dateCycleId: number, investmentId: number | null): AxiosPromise<Model.PlainTrackedObject<DateCycle>>;

    /** 
     * Saves a date cycle, and its children using tracking state.
     * @param dateCycle Date cycle to be saved.
     * @param investmentId Investment id the cycle was edited from.
     */
    saveDateCycle(dateCycle: Model.PartialPlainObject<DateCycle>, investmentId: number | null): AxiosPromise<Model.PlainTrackedObject<DateCycle>>;

    /** 
     * Deletes a date cycle and it's children
     * @param dateCycleId The ID of the date cycle to be deleted.
     */
    deleteDateCycle(dateCycleId: number): AxiosPromise;

    /** 
     * Adds a child interest rate type to the parent interest rate type and generates new interest rates for the child interest rate type based on the day offset and business days.
     * @param command The command for adding a child interest rate type to a parent interest rate type.
     * @returns The parent interest rate type with all child interest rate types.
     */
    addChildInterestRateTypeToParent(command: Model.PartialPlainObject<AddChildInterestRateTypeCommand>): AxiosPromise<Model.PlainTrackedObject<InterestRateType>>;

    // Client only properties / methods
}

@injectable()
export default class InvestmentSetupApiClient extends Data.ApiClientBase implements IInvestmentSetupApiClient {

    constructor(config = AppService.get(Types.App.Config)) {
        super(`${config.apiPath}/investment-setup`);
    }

    public getInterestRateCalculationTypes(): AxiosPromise<Array<Model.PlainTrackedObject<InterestRateCalculationType>>> {
        return this.axios.get(`${this.apiPath}/calculation-types`);
    }

    public saveInterestRateCalculationType(interestRateCalculationType: Model.PartialPlainObject<InterestRateCalculationType>): AxiosPromise<Model.PlainTrackedObject<InterestRateCalculationType>> {
        return this.axios.post(`${this.apiPath}/calculation-types`, interestRateCalculationType);
    }

    public deleteInterestRateCalculationType(interestRateCalculationTypeId: number): AxiosPromise {
        return this.axios.post(`${this.apiPath}/calculation-types/delete/${interestRateCalculationTypeId}`);
    }

    public getInterestRateTypes(includeChildren?: boolean): AxiosPromise<Array<Model.PlainTrackedObject<InterestRateType>>> {
        return this.axios.get(`${this.apiPath}/interest-rate-types/${includeChildren ?? true}`);
    }

    public getInterestRateLookupsPaged(request: Model.PartialPlainObject<Data.PageRequest<InterestRatePagedLookupCriteria>>): AxiosPromise<Data.PageResult<Model.PlainTrackedObject<InterestRate>>> {
        return this.axios.post(`${this.apiPath}/interest-rate-paged`, request);
    }

    public saveInterestRateTypes(interestRateType: Array<Model.PartialPlainObject<InterestRateType>>): AxiosPromise<Array<Model.PlainTrackedObject<InterestRateType>>> {
        return this.axios.post(`${this.apiPath}/interest-rate-types`, interestRateType);
    }

    public saveInterestRates(interestRate: Array<Model.PartialPlainObject<InterestRate>>): AxiosPromise<Array<Model.PlainTrackedObject<InterestRate>>> {
        return this.axios.post(`${this.apiPath}/interest-rates`, interestRate);
    }

    public getDateCycles(): AxiosPromise<Array<Model.PlainObject<DateCycleLookup>>> {
        return this.axios.get(`${this.apiPath}/date-cycles`);
    }

    public getDateCycle(dateCycleId: number, investmentId: number | null): AxiosPromise<Model.PlainTrackedObject<DateCycle>> {
        return this.axios.get(`${this.apiPath}/date-cycle/${dateCycleId}?investmentId=${investmentId ?? ""}`);
    }

    public saveDateCycle(dateCycle: Model.PartialPlainObject<DateCycle>, investmentId: number | null): AxiosPromise<Model.PlainTrackedObject<DateCycle>> {
        return this.axios.post(`${this.apiPath}/date-cycles?investmentId=${investmentId ?? ""}`, dateCycle);
    }
    
    public deleteDateCycle(dateCycleId: number): AxiosPromise {
        return this.axios.delete(`${this.apiPath}/date-cycle/${dateCycleId}`);
    }

    public addChildInterestRateTypeToParent(command: Model.PartialPlainObject<AddChildInterestRateTypeCommand>): AxiosPromise<Model.PlainTrackedObject<InterestRateType>> {
        return this.axios.post(`${this.apiPath}/add-child-rate-type`, command);
    }

    // Client only properties / methods
}